import React, { useState } from "react";
import { Radio, Button, Select } from "@mantine/core";
import Header from "../components/Header";
import Footer from "../components/Footer";

const CryptoDeposit = () => {
  const [reason, setReason] = useState("");
  const [step, setStep] = useState(1);
  const [transactionStatus, setTransactionStatus] = useState("");

  const reasons = [
    {
      value: "wrong-memo",
      label: "Forgot or filled in wrong memo/tag",
      description:
        "When depositing a coin to Tillionx, some networks require you to fill in a memo/tag. If you forgot or filled in the wrong memo/tag, please submit an appeal.",
    },
    {
      value: "delisted-coin",
      label: "Deposited an unlisted/delisted coin",
      description:
        "If you deposited an unlisted/delisted coin, please submit an appeal.",
    },
    {
      value: "exchange-refund",
      label: "Another exchange refunded the deposit to Tillionx",
      description:
        "If you applied for a refund on the other platform, the refund may be credited to your Tillionx wallet. Please submit an appeal to track your deposit.",
    },
    {
      value: "unknown",
      label: "Don't know / None of the above",
      description:
        "If you're unsure why your deposit hasn’t arrived, please submit an appeal.",
    },
  ];

  const selectedReason = reasons.find((r) => r.value === reason);

  return (
    <>
      <Header />
      <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-300 min-h-screen py-10">
        <div className="max-w-2xl mx-auto p-8 rounded-xl shadow-sm bg-white dark:bg-gray-800">
          {/* Title */}
          <h1 className="text-2xl font-bold mb-6">Deposit Status Query</h1>

          {/* Step 1: Radio Options */}
          {step === 1 && (
            <>
              <p className="font-medium mb-4">
                Do you know the reason why your deposit hasn&apos;t arrived?
              </p>

              <Radio.Group
                value={reason}
                onChange={setReason}
                className="space-y-3"
              >
                {reasons.map((r) => (
                  <Radio
                    key={r.value}
                    value={r.value}
                    label={r.label}
                    className="border rounded-lg px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                  />
                ))}
              </Radio.Group>

              {/* Continue button logic */}
              {reason && reason !== "unknown" && (
                <Button
                  fullWidth
                  color="yellow"
                  disabled={!reason || reason === "unknown"}
                  onClick={() => setStep(2)}
                  className="mt-4"
                >
                  Continue
                </Button>
              )}
            </>
          )}

          {/* Step 2: Dropdown + Description */}
          {step === 2 && selectedReason && (
            <>
              <p className="font-medium mb-4">
                Do you know the reason why your deposit hasn&apos;t arrived?
              </p>

              <div className="flex items-center gap-2 mb-4">
                <Select
                  data={reasons.map((r) => ({
                    value: r.value,
                    label: r.label,
                  }))}
                  value={reason}
                  onChange={setReason}
                  className="flex-1"
                  size="md"
                />
                <button
                  onClick={() => setStep(1)}
                  className="text-yellow-600 font-medium"
                >
                  Back
                </button>
              </div>

              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {selectedReason.description}
              </p>

              <Button fullWidth size="md" color="yellow">
                Submit Appeal
              </Button>
            </>
          )}

          {/* Step 1 Extra: Unknown reason handling */}
          {reason === "unknown" && step === 1 && (
            <div className="mt-6 border-t pt-6">
              <p className="font-medium mb-2">
                What is the transaction status on the withdrawal platform?
              </p>
              <p className="text-gray-500 text-sm mb-4">
                If you withdrew crypto from your Tillionx account and deposited
                it in another platform, please check the transaction status on
                the other platform.
              </p>

              <Radio.Group
                value={transactionStatus}
                onChange={setTransactionStatus}
                className="space-y-2"
              >
                <Radio
                  value="successful"
                  label="Successful/Completed"
                  className="cursor-pointer mb-4"
                />
                <Radio
                  value="pending"
                  label="Pending/Failed"
                  className="cursor-pointer"
                />
              </Radio.Group>

              {transactionStatus === "successful" && (
                <div className="mt-6">
                  <label className="block font-medium mb-2">
                    Enter TxID/TxHash
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter TxID/TxHash"
                      className="flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    />
                    <Button color="yellow" size="md">
                      Search
                    </Button>
                  </div>
                  <a
                    href="#"
                    className="text-sm text-yellow-600 hover:underline block mt-2"
                  >
                    How to Find My Transaction ID (TxID)
                  </a>
                </div>
              )}
              {transactionStatus === "pending" && (
                <div className="mt-6">
                  <p className="text-gray-500 text-sm mb-2">
                    If your transaction status is pending/failed on the other
                    platform, please contact the other platform for more
                    information. We are unable to retrieve assets that have not
                    reached our platform.
                  </p>
                  <Button fullWidth size="md" color="yellow">
                    I Understand
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Application Record Section */}
        <div className="max-w-2xl mx-auto mt-12 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-sm text-center">
          <h2 className="text-xl font-semibold mb-6">
            Deposit Recovery Application Record
          </h2>

          {/* Empty state */}
          <div className="flex flex-col items-center text-gray-500">
            <img
              src="https://img.icons8.com/ios/100/000000/document--v1.png"
              alt="No record"
              className="w-20 h-20 mb-3 opacity-60"
            />
            <p>No recent records found</p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CryptoDeposit;
